import { useEffect, useState } from "react";
import { MousePointer2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Self-playing product preview: cursor travels to a button, clicks, a numbered marker drops,
 * a comment card types out, then it lands on the board as "In Progress · Rahul".
 * Pure DOM/CSS — no video or image assets.
 */
const PHASES = ["idle", "move", "click", "marker", "comment", "assigned"] as const;
type Phase = (typeof PHASES)[number];
const DURATION: Record<Phase, number> = { idle: 900, move: 1100, click: 450, marker: 700, comment: 1800, assigned: 2600 };
const COMMENT = "Please change this button to green.";

export function ProductDemo({ className, interactive = true }: { className?: string; interactive?: boolean }) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [typed, setTyped] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setPhase("assigned");
      setTyped(COMMENT.length);
      return;
    }
    if (paused) return;
    const t = setTimeout(() => {
      const i = PHASES.indexOf(phase);
      const next = PHASES[(i + 1) % PHASES.length]!;
      if (next === "idle") setTyped(0);
      setPhase(next);
    }, DURATION[phase]);
    return () => clearTimeout(t);
  }, [phase, paused]);

  useEffect(() => {
    if (phase !== "comment" || typed >= COMMENT.length) return;
    const t = setTimeout(() => setTyped((n) => n + 1), 38);
    return () => clearTimeout(t);
  }, [phase, typed]);

  const at = PHASES.indexOf(phase);
  const showMarker = at >= PHASES.indexOf("marker");
  const showCard = at >= PHASES.indexOf("comment");
  const assigned = phase === "assigned";
  const cursorOnButton = at >= PHASES.indexOf("move");

  return (
    <div
      className={cn("relative select-none", className)}
      onMouseEnter={() => interactive && setPaused(false)}
      aria-label="Animated preview: a client clicks a button on a website, a numbered feedback marker appears with the comment 'Please change this button to green.', assigned to Rahul, status In Progress."
      role="img"
    >
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(15,23,42,.45)]">
        {/* Browser chrome */}
        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2.5">
          <span className="flex gap-1.5">
            <span className="size-2.5 rounded-full bg-[#FF5F57]" />
            <span className="size-2.5 rounded-full bg-[#FEBC2E]" />
            <span className="size-2.5 rounded-full bg-[#28C840]" />
          </span>
          <span className="mx-auto flex h-6 w-full max-w-xs items-center justify-center gap-1.5 rounded-md bg-white text-[11px] text-slate-500 ring-1 ring-slate-200">
            🔒 staging.abcfurniture.in
          </span>
        </div>
        {/* Fake site */}
        <div className="relative bg-[#fbf7f1] px-6 pb-8 pt-5 sm:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex size-6 items-center justify-center rounded bg-[#9a5b2f] text-[9px] font-bold text-white">ABC</span>
              <span className="font-serif text-[13px] font-semibold text-[#2b2118]">ABC Furniture</span>
            </div>
            <div className="hidden gap-4 text-[11px] text-[#5a4a3b] sm:flex">
              <span>Living</span>
              <span>Bedroom</span>
              <span>About</span>
            </div>
          </div>
          <div className="mt-7 grid gap-6 sm:grid-cols-[1.1fr_1fr]">
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[2px] text-[#9a5b2f]">Festive collection</p>
              <p className="mt-2 font-serif text-[22px] font-semibold leading-tight text-[#2b2118] sm:text-[26px]">Furniture that feels like home</p>
              <p className="mt-2 text-[11.5px] leading-relaxed text-[#5a4a3b]">Solid sheesham wood, handcrafted in Jodhpur. Free delivery in 12 cities.</p>
              <div className="relative mt-4 inline-block">
                <span
                  className={cn(
                    "inline-block rounded-md px-4 py-2 text-[12px] font-semibold text-white transition-all duration-300",
                    assigned ? "bg-[#16a34a]" : "bg-[#9a5b2f]",
                    phase === "click" && "scale-95",
                    (phase === "move" || phase === "click") && "ring-2 ring-brand-600 ring-offset-2",
                  )}
                >
                  Shop the Collection
                </span>
                {showMarker && (
                  <span className="absolute -right-3 -top-3 flex size-7 animate-pop-in items-center justify-center rounded-full rounded-bl-[4px] border-2 border-white bg-brand-600 text-[12px] font-bold text-white shadow-lg">
                    1
                  </span>
                )}
              </div>
            </div>
            <div className="hidden aspect-[5/4] rounded-lg bg-[#efe3d2] sm:block">
              <svg viewBox="0 0 300 240" className="h-full w-full" aria-hidden>
                <ellipse cx="150" cy="212" rx="120" ry="10" fill="#e3d4bf" />
                <rect x="40" y="110" width="220" height="70" rx="16" fill="#c98b5a" />
                <rect x="55" y="70" width="190" height="60" rx="14" fill="#8a5530" />
                <rect x="28" y="100" width="34" height="80" rx="12" fill="#8a5530" />
                <rect x="238" y="100" width="34" height="80" rx="12" fill="#8a5530" />
              </svg>
            </div>
          </div>

          {/* Cursor */}
          <MousePointer2
            className={cn(
              "absolute size-5 fill-ink text-white drop-shadow transition-all duration-[1000ms] ease-in-out",
              cursorOnButton ? "left-[150px] top-[178px] sm:left-[170px] sm:top-[192px]" : "left-[78%] top-[85%]",
              phase === "click" && "scale-90",
              showCard && "opacity-0",
            )}
          />
        </div>
      </div>

      {/* Comment card */}
      <div
        className={cn(
          "absolute left-[46%] top-[42%] w-[min(290px,60%)] rounded-xl border border-slate-200 bg-white p-3.5 shadow-pop transition-all duration-300 sm:left-[52%]",
          showCard ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0",
        )}
      >
        <div className="flex items-center gap-2">
          <span className="flex size-6 items-center justify-center rounded-full bg-blue-100 text-[10px] font-bold text-blue-800">AM</span>
          <span className="text-[12px] font-semibold text-ink">Arjun · ABC Furniture</span>
          <span className="ml-auto rounded bg-ink px-1.5 text-[10px] font-bold text-white">#1</span>
        </div>
        <p className="mt-2 min-h-[2.6em] text-[13px] leading-snug text-slate-800">
          {COMMENT.slice(0, typed)}
          {phase === "comment" && typed < COMMENT.length && <span className="ml-0.5 inline-block h-3.5 w-px animate-pulse bg-ink align-middle" />}
        </p>
        <div className={cn("mt-2.5 flex items-center justify-between border-t border-slate-100 pt-2.5 text-[11.5px] transition-opacity", assigned ? "opacity-100" : "opacity-0")}>
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="flex size-5 items-center justify-center rounded-full bg-amber-100 text-[9px] font-bold text-amber-800">RV</span>
            Assigned to <strong className="text-ink">Rahul</strong>
          </span>
          <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800">● In Progress</span>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-center gap-3 text-[11.5px] text-slate-500">
        <span>Desktop · Chrome · 1440×900 · /</span>
        <span aria-hidden>·</span>
        <span>Screenshot captured ✓</span>
      </div>
    </div>
  );
}
